"use client";

import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Target01Icon, EyeIcon } from "@hugeicons/core-free-icons";

export default function MissionVision() {
  const panelVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 18,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <section className="min-h-screen flex justify-center items-center py-24 relative px-4 bg-neutral-50 dark:bg-black transition-colors duration-500">
      {/* Background ambient accents */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-blue-500/5 dark:bg-blue-500/[0.02] blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 -translate-y-1/2 w-[550px] h-[550px] rounded-full bg-indigo-500/5 dark:bg-indigo-500/[0.02] blur-[150px] pointer-events-none" />

      <div className="flex flex-col lg:flex-row gap-16 justify-center items-center max-w-6xl w-full z-10">
        
        {/* Left Side: Brand Statement */}
        <motion.div
          variants={panelVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-120px" }}
          className="flex-1 space-y-6 lg:text-left text-center"
        >
          <div className="inline-block px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wider text-blue-600 dark:text-blue-400 bg-blue-500/10 uppercase">
            Ethos & Drive
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white font-recoleta leading-tight">
            We don&#39;t follow trends. We engineer standards.
          </h2>
          <p className="text-neutral-600 dark:text-neutral-300 font-manjari font-light text-lg sm:text-xl leading-relaxed">
            At Devebsy, we sit at the intersection of creative digital expression and highly 
            optimized computing systems. We believe every pixel must serve a purpose, and every 
            line of code must execute at optimal speeds.
          </p>
        </motion.div>

        {/* Right Side: Mission & Vision Cards */}
        <div className="flex-1 flex flex-col gap-6 w-full max-w-lg">
          {/* Mission Card */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            whileHover={{ y: -6, scale: 1.015 }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            className="cursor-pointer"
          >
            <div className="border border-neutral-200/50 dark:border-neutral-800/40 bg-white/70 dark:bg-neutral-900/10 backdrop-blur-xl shadow-xl hover:shadow-2xl hover:border-blue-500/20 dark:hover:border-blue-500/10 transition-all duration-300 rounded-3xl p-8 flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                <HugeiconsIcon icon={Target01Icon} className="w-6 h-6" size={24} />
              </div>
              <div className="space-y-2 text-left">
                <h3 className="text-xl font-bold text-slate-800 dark:text-white font-recoleta">
                  The Blueprint (Mission)
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed font-outfit font-medium">
                  To build next-generation interfaces and backend micro-architectures that empower 
                  fast-scaling brands to load instantly, convert fluidly, and maintain extreme stability.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Vision Card */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            whileHover={{ y: -6, scale: 1.015 }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            className="cursor-pointer"
          >
            <div className="border border-neutral-200/50 dark:border-neutral-800/40 bg-white/70 dark:bg-neutral-900/10 backdrop-blur-xl shadow-xl hover:shadow-2xl hover:border-purple-500/20 dark:hover:border-purple-500/10 transition-all duration-300 rounded-3xl p-8 flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                <HugeiconsIcon icon={EyeIcon} className="w-6 h-6" size={24} />
              </div>
              <div className="space-y-2 text-left">
                <h3 className="text-xl font-bold text-slate-800 dark:text-white font-recoleta">
                  The Horizon (Vision)
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed font-outfit font-medium">
                  To set the standard of modern interaction engineering, rendering static code obsolete 
                  and defining immersive, lightning-fast digital interactions as the new baseline.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
}
